import { sanitizeUrl } from 'utils/sanitizeUrl';
import { type OfferData } from 'service/offer/model';
import { CATEGORY_ALL_CATEGORIES, type CategoryData, type OfferCategoryData } from 'service/category/model.ts';
import {
	CATEGORY_ALL_CATEGORIES_SEO_NAME,
	DEFAULT_RADIUS_VALUE,
	KNOWN_LOCALITIES,
	getKnownLocalityByLocality,
	LOCALITY_CELA_CR,
	LOCALITY_CELA_CR_SEO_NAME,
	LOCALITY_TYPES,
	MAX_OFFERS_PAGE,
	PRICE,
	SORT_BY_KEYS,
	SORT_BY_SEO_NAMES,
	SORT_SEO_NAMES_VALUES,
	SORT_BY_SORT,
	SORT_KEYS_BY_SEO_NAME,
	type TLocality,
	type TPrice,
	type TSortBySeoNames,
	type TSortBySort,
	UPCOMING_EXPIRING_URL_VALUE,
} from 'utils/constants';
import isEmpty from 'lodash/isEmpty';
import type { ShopData } from 'service/shop/model';
import { categoryTreeToCategory } from 'components/modal/categories/signals.ts';
import type { UserData } from 'service/user/model';
import type { PremiseData } from 'service/premise/model';
import type { UnpackedOfferData } from 'service/unpacked/model.ts';
import { baseUrl } from 'utils/env';
import type { LocalityData } from 'service/locality/model';
import { STATE, type TState } from 'utils/state.ts';
import { getDefaultRadius, getKdeParameter } from 'utils/locality';
import { roundedTimestamp } from './date';
import { memoizedCategories } from 'signals/category';
import { KnownLocalityNotFoundError } from 'errors/KnownLocalityNotFoundError.ts';
import { toastMessages } from 'components/toastMessage/signals';
import { CategoryNotFoundError } from 'errors/CategoryNotFoundError';
import { PageNumberTooHighError } from 'errors/PageNumberTooHighError';
import { PageNumberNanError } from 'errors/PageNumberNanError';
import { isSSR } from 'utils/isSSR';

const sortBySeoNamesValues = Object.values(SORT_BY_SEO_NAMES);

export function getBazarPath(path: string, search: string) {
	const pathParts = path.split('/').filter(Boolean);
	const newPath = '/bazar/' + pathParts.shift();
	const params = new URLSearchParams(search);

	if (pathParts[0] && sortBySeoNamesValues.includes(pathParts[0] as TSortBySeoNames)) {
		params.set('sort', pathParts[0]);
		pathParts.shift();
	}

	if (pathParts[0] && Number(pathParts[0]) > 1) {
		params.set('page', pathParts[0]);
	}

	const newParams = params.toString();

	return newPath + (newParams ? '?' + newParams : '');
}

export function getUnpackedOfferId(offerSeo: string): string {
	const offerId = offerSeo.split('-').pop();

	if (!offerId) {
		throw new Error(`Invalid offerId: ${offerSeo}`);
	}

	return offerId;
}

export function getOfferId(offerSeo: string): string {
	const offerId = getOfferIdFromSeoName(offerSeo);

	if (isNaN(Number.parseInt(offerId, 10))) {
		throw new Error(`Invalid offerId: ${offerSeo}`);
	}

	return offerId;
}

function getOfferIdFromSeoName(seoName: string): string {
	return parseInt(seoName, 10).toString();
}

export function mapyCzUrl(entityId: number | string, entityType: string) {
	return `https://mapy.cz?id=${entityId}&source=${entityType.substring(0, 4)}`;
}

export function shouldRedirectMirroredOffer(offerSeo: string, offer: OfferData) {
	return offer.isOfferMirroring && offer.seoName !== offerSeo;
}

export function generateOfferDetailLink(
	{ seoName }: Partial<OfferData>,
	params: Record<string, string> = {},
	absolute?: boolean,
): string | undefined {
	if (!seoName) {
		return undefined;
	}

	return absolute
		? generateLink(`${baseUrl()}/inzerat/${seoName}`, params)
		: generateRelativeLink(`/inzerat/${seoName}`, params);
}

export function generateUnpackedShopLink(
	shop: ShopData,
	params: Record<string, string> = {},
	absolute?: boolean,
): string {
	const path = `/eshop-rozbalene/${shop.id}`;
	return absolute ? generateLink(`${baseUrl()}${path}`, params) : generateRelativeLink(path, params);
}

export function generateUnpackedOfferDetailLink(
	unpackedOffer: UnpackedOfferData,
	params: Record<string, string> = {},
	absolute?: boolean,
): string {
	const path = `/rozbalena-nabidka/${unpackedOffer.seoName}-${unpackedOffer.id}`;
	return absolute ? generateLink(`${baseUrl()}${path}`, params) : generateRelativeLink(path, params);
}

export function generateRelativeLink(base: string, params: Record<string, string> = {}): string {
	const placeholderDomain = 'http://example.com';
	const url = new URL(base, placeholderDomain);
	const searchParams = new URLSearchParams(params);
	url.search = searchParams.toString();
	return sanitizeUrl(url.toString().replace(placeholderDomain, ''));
}

export function generateLink(base: string, params: Record<string, string> = {}): string {
	const url = new URL(base);
	const searchParams = new URLSearchParams(params);
	url.search = searchParams.toString();
	return sanitizeUrl(url.toString());
}

export function generateLinkFromLocality(
	locality: LocalityData,
	radius?: number,
	base = baseUrl() + '/' + CATEGORY_ALL_CATEGORIES_SEO_NAME,
) {
	const {
		entityType,
		municipalityId,
		citypart,
		municipality,
		districtId,
		quarter,
		quarterId,
		quarterSeoName,
		regionId,
		regionSeoName,
		wardId,
		wardSeoName,
		municipalitySeoName,
		districtSeoName,
	} = locality;
	let linkEntityId;
	let linkEntityType;
	let linkSeoName = '';

	switch (entityType) {
		// compatible with getDisplayedOfferLocality function
		case LOCALITY_TYPES.ADDRESS:
			if (citypart) {
				if (citypart === quarter && quarterId && quarterSeoName) {
					linkSeoName = quarterSeoName;
					linkEntityId = quarterId;
					linkEntityType = LOCALITY_TYPES.QUARTER;
					break;
				}

				if (municipality !== '' && citypart === municipality && municipalityId && municipalitySeoName) {
					linkSeoName = municipalitySeoName;
					linkEntityId = municipalityId;
					linkEntityType = LOCALITY_TYPES.MUNICIPALITY;
					break;
				}
			}

			if (wardId && wardSeoName) {
				linkSeoName = wardSeoName;
				linkEntityId = wardId;
				linkEntityType = LOCALITY_TYPES.WARD;
				break;
			} else if (quarterId && quarterSeoName) {
				linkSeoName = quarterSeoName;
				linkEntityId = quarterId;
				linkEntityType = LOCALITY_TYPES.QUARTER;
				break;
			} else if (municipalityId && municipalitySeoName) {
				linkSeoName = municipalitySeoName;
				linkEntityId = municipalityId;
				linkEntityType = LOCALITY_TYPES.MUNICIPALITY;
				break;
			} else if (districtId && districtSeoName) {
				linkSeoName = districtSeoName;
				linkEntityId = districtId;
				linkEntityType = LOCALITY_TYPES.DISTRICT;
				break;
			} else if (regionId && regionSeoName) {
				linkSeoName = regionSeoName;
				linkEntityId = regionId;
				linkEntityType = LOCALITY_TYPES.REGION;
				break;
			} else {
				linkSeoName = municipalitySeoName;
				linkEntityId = municipalityId;
				linkEntityType = LOCALITY_TYPES.MUNICIPALITY;
			}

			break;

		case LOCALITY_TYPES.MUNICIPALITY:
			linkSeoName = municipalitySeoName;
			linkEntityId = municipalityId;
			linkEntityType = LOCALITY_TYPES.MUNICIPALITY;
			break;

		case LOCALITY_TYPES.DISTRICT:
			linkEntityId = districtId;
			linkEntityType = entityType;
			linkSeoName =
				getKnownLocalityByLocality({ ...locality, entityId: linkEntityId, entityType: linkEntityType })
					?.seoName ?? locality.districtSeoName;
			break;

		case LOCALITY_TYPES.WARD:
		case LOCALITY_TYPES.QUARTER:
			if (wardId && wardSeoName) {
				linkSeoName = wardSeoName;
				linkEntityId = wardId;
				linkEntityType = LOCALITY_TYPES.WARD;
			} else if (quarterId && quarterSeoName) {
				linkSeoName = quarterSeoName;
				linkEntityId = quarterId;
				linkEntityType = LOCALITY_TYPES.QUARTER;
			} else if (municipalityId && municipalitySeoName) {
				linkSeoName = municipalitySeoName;
				linkEntityId = municipalityId;
				linkEntityType = LOCALITY_TYPES.MUNICIPALITY;
			} else {
				linkSeoName = districtSeoName;
				linkEntityId = districtId;
				linkEntityType = LOCALITY_TYPES.DISTRICT;
			}
			break;

		case LOCALITY_TYPES.REGION:
			linkEntityId = regionId;
			linkEntityType = entityType;
			linkSeoName =
				getKnownLocalityByLocality({ ...locality, entityId: linkEntityId, entityType: linkEntityType })
					?.seoName ?? locality.regionSeoName;
			break;
	}

	const link = new URL(`${base}/${linkSeoName}`);

	if (linkEntityId && linkEntityType) {
		const updatedLocality = { ...locality, entityId: linkEntityId, entityType: linkEntityType };
		const kdeParameter = getKdeParameter(updatedLocality);
		if (kdeParameter) {
			link.searchParams.append('kde', kdeParameter);
		}

		const localityRadius = radius || getDefaultRadius();
		if (localityRadius !== DEFAULT_RADIUS_VALUE) link.searchParams.append('radius', `${localityRadius}`);
	}

	return link.toString();
}

export const PARAM_KEYS = {
	QUERY: 'hledej',
	CATEGORY: 'category',
	EXPIRING: 'expiring',
	LOCALITY: 'locality',
	PRICE: 'price',
	SORT: 'sort',
	PAGE: 'page',
	WHERE: 'kde',
	RADIUS: 'radius',
	WITH_PRICE_AGREEMENT: 'dohodou',
	ONLY_UNPACKED: 'rozbalene',
	WATCHDOG_ID: 'watchdog_id',
	WATCHDOG_HASH: 'watchdog_hash',
	WATCHDOG_TIMESTAMP: 'watchdog_timestamp',
} as const;
export type TParamKeys = (typeof PARAM_KEYS)[keyof typeof PARAM_KEYS];

export const allQueryIsParamPartOfPath: [TParamKeys, boolean][] = [
	[PARAM_KEYS.QUERY, false],
	[PARAM_KEYS.CATEGORY, true],
	[PARAM_KEYS.LOCALITY, false],
	[PARAM_KEYS.PRICE, false],
	[PARAM_KEYS.SORT, false],
	[PARAM_KEYS.PAGE, false],
	[PARAM_KEYS.WHERE, false],
	[PARAM_KEYS.RADIUS, false],
	[PARAM_KEYS.WITH_PRICE_AGREEMENT, false],
	[PARAM_KEYS.ONLY_UNPACKED, false],
];

export const isParamPartOfPath: [TParamKeys, boolean][] = [
	[PARAM_KEYS.QUERY, true],
	[PARAM_KEYS.CATEGORY, true],
	[PARAM_KEYS.LOCALITY, true],
	[PARAM_KEYS.PRICE, true],
	[PARAM_KEYS.SORT, true],
	[PARAM_KEYS.PAGE, true],
	[PARAM_KEYS.WHERE, false],
	[PARAM_KEYS.RADIUS, false],
	[PARAM_KEYS.WITH_PRICE_AGREEMENT, false],
	[PARAM_KEYS.ONLY_UNPACKED, false],
	[PARAM_KEYS.WATCHDOG_ID, false],
	[PARAM_KEYS.WATCHDOG_HASH, false],
	[PARAM_KEYS.WATCHDOG_TIMESTAMP, false],
] as const;

export type FilterParam = {
	[k: string]: any;
	seoName?: string;
};

export type FilerPrice = {
	seoName: string;
	from?: number;
	to?: number;
	type: TPrice;
};

type CategoryFilterData = Partial<CategoryData> & {
	id: number;
	seoName: string;
	title: string;
};

export type CategoryFilter = CategoryFilterData | undefined;

export type URLFilterParams = {
	[k in TParamKeys]: FilterParam | undefined;
} & {
	hledej: {
		seoName?: string;
	};
	category?: CategoryFilter;
	expiring: {
		value: typeof UPCOMING_EXPIRING_URL_VALUE | undefined;
	};
	locality: TLocality;
	price: FilerPrice;
	sort: {
		sort: TSortBySort;
		seoName: TSortBySeoNames;
	};
	page: {
		seoName: string;
		value: number;
	};
	status?: TState;
	timestampTo?: number;
	isBazarek?: boolean;
	seller?: UserData | PremiseData;
	limit?: number;
	isAdmin?: boolean;
};

export const defaultFilterParams: URLFilterParams = {
	hledej: {
		seoName: undefined,
	},
	category: undefined,
	expiring: {
		value: undefined,
	},
	locality: {
		seoName: 'cela-cr',
		name: '',
		id: 0,
		type: LOCALITY_TYPES.ADDRESS,
	},
	price: {
		from: undefined,
		to: undefined,
		type: PRICE.NO_LIMIT,
		seoName: PRICE.NO_LIMIT,
	},
	sort: {
		sort: SORT_BY_SORT.NEWEST,
		seoName: SORT_BY_SEO_NAMES.NEWEST,
	},
	page: {
		seoName: '1',
		value: 1,
	},
	status: STATE.ACTIVE,
	timestampTo: roundedTimestamp(),
	isBazarek: false,
	seller: undefined,
	limit: 12,
	isAdmin: false,
	kde: undefined,
	radius: undefined,
	dohodou: undefined,
	rozbalene: undefined,
	watchdog_id: undefined,
	watchdog_hash: undefined,
	watchdog_timestamp: undefined,
};

const regex = /^(\d+)/;

export function getCategory(category: string, defaultCategory?: CategoryData) {
	const foundCategory = memoizedCategories.categoriesMap[Number(regex.exec(category)?.[0] || 0)];

	if (!foundCategory) {
		if (
			defaultCategory ||
			category === CATEGORY_ALL_CATEGORIES_SEO_NAME ||
			category === CATEGORY_ALL_CATEGORIES.id.toString() ||
			isSSR()
		) {
			return defaultCategory ?? CATEGORY_ALL_CATEGORIES;
		} else {
			throw new CategoryNotFoundError(`Category not found: ${category}`);
		}
	}

	return categoryTreeToCategory(foundCategory);
}

export function getLocality(localityUrlSeoName: string, kde?: string): TLocality {
	if (localityUrlSeoName === LOCALITY_CELA_CR_SEO_NAME) {
		return LOCALITY_CELA_CR;
	}

	if (kde) {
		const entityTypes = Object.values(LOCALITY_TYPES);

		const [localityUrlType, localityUrlId]: [string, string] = [
			kde?.match(/^[a-z]+/)?.[0] || '',
			kde?.match(/\d+$/)?.[0] || '',
		];

		if (!localityUrlId || !localityUrlType) {
			throw new KnownLocalityNotFoundError(
				`Failed parsing URL locality: ${localityUrlSeoName}, invalid "kde": ${kde} parameter`,
			);
		}

		const localityType = entityTypes.find((type) => type.startsWith(localityUrlType)) || LOCALITY_TYPES.ADDRESS;

		return {
			seoName: localityUrlSeoName,
			name: '',
			id: Number(localityUrlId),
			type: localityType,
		};
	} else {
		const foundLocality = KNOWN_LOCALITIES.find(({ seoName }) => seoName === localityUrlSeoName);

		if (foundLocality) return { ...foundLocality };

		throw new KnownLocalityNotFoundError(
			`Failed parsing URL locality: "kde" was not supplied and there is no known locality with seoName: ${localityUrlSeoName}.`,
		);
	}
}

const rgxPrice = /^(cena-(neomezena|od-\d+-kc|do-\d+-kc|od-\d+-do-\d+-kc))|(zdarma)$/;

function getPrice(price?: string): FilerPrice {
	if (!price || !rgxPrice.test(price)) {
		return {
			type: PRICE.NO_LIMIT,
			seoName: PRICE.NO_LIMIT,
		};
	}

	let from = undefined,
		to = undefined,
		type: TPrice = PRICE.NO_LIMIT;
	const priceParts = price.split('-');

	switch (priceParts.length) {
		case 1:
			type = PRICE.FREE;
			break;
		case 4:
			if (priceParts[1] === 'od') {
				from = Number(priceParts[2]);
				type = PRICE.FROM;
				break;
			}
			to = Number(priceParts[2]);
			type = PRICE.TO;
			break;
		case 6:
			from = Number(priceParts[2]);
			to = Number(priceParts[4]);
			type = PRICE.BETWEEN;
			break;
		default:
			break;
	}

	return {
		from,
		to,
		type,
		seoName: price,
	};
}

export function getSort(sort?: string) {
	const sortKey = SORT_KEYS_BY_SEO_NAME[sort as TSortBySeoNames];
	if (!(sort && SORT_BY_KEYS.includes(sortKey))) {
		return {
			sort: SORT_BY_SORT.NEWEST,
			seoName: SORT_BY_SEO_NAMES.NEWEST,
		};
	}

	return {
		sort: SORT_BY_SORT[sortKey],
		seoName: SORT_BY_SEO_NAMES[sortKey],
	};
}

export function getPriceType(fromPrice: number | undefined, toPrice: number | undefined, isFree: boolean) {
	if (isFree) {
		return PRICE.FREE;
	}

	const { from, to } = { from: fromPrice, to: toPrice };

	if (from && to) {
		return PRICE.BETWEEN;
	}
	if (from && !to) {
		return PRICE.FROM;
	}
	if (to && !from) {
		return PRICE.TO;
	}
	return PRICE.NO_LIMIT;
}

export function getPriceSeoName(fromPrice: number | undefined, toPrice: number | undefined, type: TPrice) {
	if (type === PRICE.FREE) {
		return PRICE.FREE;
	}
	if (fromPrice && toPrice) {
		return `cena-od-${fromPrice}-do-${toPrice}-kc`;
	}
	if (fromPrice && !toPrice) {
		return `cena-od-${fromPrice}-kc`;
	}
	if (toPrice && !fromPrice) {
		return `cena-do-${toPrice}-kc`;
	}
	return PRICE.NO_LIMIT;
}

export function urlQueryDecoder(url: URL): URLFilterParams {
	try {
		const params = Object.fromEntries(new URLSearchParams(url.search));

		const locality = getLocality(params[PARAM_KEYS.LOCALITY] || LOCALITY_CELA_CR_SEO_NAME, params.kde);
		const price = getPrice(params[PARAM_KEYS.PRICE]);
		const sort = getSort(params[PARAM_KEYS.SORT] || undefined);
		const pageNumber = Number(params[PARAM_KEYS.PAGE] || 1);

		if (isNaN(pageNumber)) {
			throw new PageNumberNanError('Invalid URL: Page number is not a number');
		}

		const page = {
			seoName: pageNumber.toString(),
			value: pageNumber,
		};

		if (page.value > MAX_OFFERS_PAGE) {
			throw new PageNumberTooHighError('Invalid URL: Page number is too high');
		}

		const watchdog_hash = { seoName: params.watchdog_hash };
		const watchdog_id = { seoName: params.watchdog_id };
		const watchdog_timestamp = { seoName: params.watchdog_timestamp };

		const category = params.categoryId ? getCategory(params.categoryId) : undefined;

		return {
			category: category!,
			expiring: {
				value:
					params[PARAM_KEYS.EXPIRING] === String(UPCOMING_EXPIRING_URL_VALUE)
						? UPCOMING_EXPIRING_URL_VALUE
						: undefined,
			},
			hledej: {
				seoName: undefined,
			},
			locality,
			price,
			sort,
			page,
			watchdog_hash,
			watchdog_id,
			watchdog_timestamp,
			kde: {
				seoName: params.kde || undefined,
			},
			radius: {
				seoName: params.radius || undefined,
			},
			dohodou: {
				seoName: params['cena-dohodou'] || undefined,
			},
			rozbalene: {
				seoName: params['rozbalene'] || undefined,
			},
		};
	} catch (error: any) {
		if (!isSSR()) {
			toastMessages.oops();
		}

		throw error;
	}
}

export function urlDecoder(url: URL): URLFilterParams {
	const path = url.pathname.split('/').filter(Boolean);
	let hledej: FilterParam = {};

	const isHledejRozbalene = !!(path.length && path[0] === 'hledej-rozbalene');
	const isRozbaleneNabidky = !!(path.length && path[0] === 'rozbalene-nabidky');
	if (isRozbaleneNabidky) {
		path.shift();
	} else if (path.length && (path[0] === PARAM_KEYS.QUERY || isHledejRozbalene)) {
		path.shift();
		hledej = {
			seoName: decodeURIComponent(path.shift() ?? ''),
		};
	}

	try {
		const params = Object.fromEntries(new URLSearchParams(url.search));
		const hasUnpackedOffers = params['rozbalene'] === 'ano' || isHledejRozbalene || isRozbaleneNabidky;

		let sort = getSort();
		let category = getCategory(CATEGORY_ALL_CATEGORIES_SEO_NAME);
		let locality = getLocality(LOCALITY_CELA_CR_SEO_NAME);
		let price = getPrice();
		let page = {
			seoName: '1',
			value: 1,
		};

		let nextSeoName = path.shift();
		if (nextSeoName && SORT_SEO_NAMES_VALUES.includes(nextSeoName as TSortBySeoNames)) {
			sort = getSort(nextSeoName);
		} else if (nextSeoName?.startsWith('cena') || nextSeoName === 'zdarma') {
			price = getPrice(nextSeoName);
		} else if (nextSeoName?.match(/^\d+$/)) {
			page = {
				seoName: nextSeoName,
				value: Number(nextSeoName),
			};
		} else {
			category = getCategory(params.categoryId || nextSeoName || CATEGORY_ALL_CATEGORIES_SEO_NAME);
			delete params.categoryId;

			nextSeoName = path.shift();
			// nextSeoName could be 0/1 => match it as default locality https://youtrack.seznam.net/issue/SBA-1094/Presmerovat-nevalidni-URL-do-hledani
			const isNextSeoAsLocalityId = nextSeoName?.match(/^\d+$/) || nextSeoName === 'undefined';
			const isNextSeoAsPrice = nextSeoName?.startsWith('cena') || nextSeoName === 'zdarma';
			locality =
				isNextSeoAsPrice || isNextSeoAsLocalityId || hasUnpackedOffers // unpacked offers are always in the whole CR
					? getLocality(LOCALITY_CELA_CR_SEO_NAME)
					: getLocality(nextSeoName || LOCALITY_CELA_CR_SEO_NAME, params.kde);
			price = isNextSeoAsPrice ? getPrice(nextSeoName) : getPrice(path.shift());
			sort = getSort(path.shift());
			const pageNumber = Number(path.shift() || 1);

			if (isNaN(pageNumber)) {
				throw new PageNumberNanError('Invalid URL: Page number is not a number');
			}

			page = {
				seoName: pageNumber.toString(),
				value: pageNumber,
			};
		}

		if (page?.value && page.value > MAX_OFFERS_PAGE) {
			throw new PageNumberTooHighError('Invalid URL: Page number is too high');
		}

		const watchdog_hash = { seoName: params.watchdog_hash };
		const watchdog_id = { seoName: params.watchdog_id };
		const watchdog_timestamp = { seoName: params.watchdog_timestamp };

		return {
			hledej,
			category,
			expiring: {
				value:
					params[PARAM_KEYS.EXPIRING] === String(UPCOMING_EXPIRING_URL_VALUE)
						? UPCOMING_EXPIRING_URL_VALUE
						: undefined,
			},
			locality,
			price,
			sort,
			page,
			watchdog_hash,
			watchdog_id,
			watchdog_timestamp,
			kde: {
				seoName: (!hasUnpackedOffers && params.kde) || undefined, // unpacked offers are always in the whole CR
			},
			radius: {
				seoName: params.radius || undefined,
			},
			dohodou: {
				seoName: params['cena-dohodou'] || undefined,
			},
			rozbalene: {
				seoName: params['rozbalene'] || (isHledejRozbalene && 'ano') || undefined,
			},
		};
	} catch (error: any) {
		throw error;
	}
}

const DEFAULT_FILTER_URL_VALUES = [
	CATEGORY_ALL_CATEGORIES_SEO_NAME,
	LOCALITY_CELA_CR_SEO_NAME,
	PRICE.NO_LIMIT,
	SORT_BY_SEO_NAMES.NEWEST,
	'1',
] as const;

const DEFAULT_FILTER_URL_PARAMS: { [key in TParamKeys]?: { seoName: string } } = {
	category: { seoName: CATEGORY_ALL_CATEGORIES_SEO_NAME },
	locality: { seoName: LOCALITY_CELA_CR_SEO_NAME },
	price: { seoName: PRICE.NO_LIMIT },
	sort: { seoName: SORT_BY_SEO_NAMES.NEWEST },
	page: { seoName: '1' },
};

function combinePath(path: string[], isFullPath: boolean) {
	path = path.filter((p) => !isEmpty(p));

	if (!isFullPath) {
		let j = DEFAULT_FILTER_URL_VALUES.length - 1;
		for (let i = path.length - 1; i > 0; i--) {
			if (path[i] === DEFAULT_FILTER_URL_VALUES[j-- as keyof typeof DEFAULT_FILTER_URL_VALUES]) {
				path.pop();
			} else break;
		}
	}

	return path.join('/');
}

export function getSearchPageUrl({
	category,
	searchQuery,
}: {
	category?: CategoryData | OfferCategoryData | CategoryFilterData | null;
	searchQuery?: string;
}) {
	return urlEncoder({
		...defaultFilterParams,
		category: category
			? {
					id: category.id,
					seoName: category.seoName,
					title: 'title' in category ? category.title : category.name,
				}
			: undefined,
		hledej: { seoName: searchQuery },
	});
}

export function urlEncoder(
	params: URLFilterParams,
	placement: typeof isParamPartOfPath | false = isParamPartOfPath,
	defaultPath?: string | false,
	isFullPath: boolean = false, // In case we ever need a full path, set this to true (keeps default values)
): string {
	// Enable temporary placement for Bazarek until it is fully implemented
	if (placement === false || (params.seller !== undefined && !params.isBazarek)) {
		placement = Object.values(PARAM_KEYS).map((key) => [key, false]);

		if (globalThis?.window && defaultPath === undefined) defaultPath = globalThis.window.location.pathname;
	}

	// encode params that are part of the path to url path
	const path: string[] = defaultPath ? [defaultPath] : [];
	const query: Record<string, string> = {};

	placement.forEach(([key, isPartUsed = false]) => {
		const { seoName } = params[key] ?? DEFAULT_FILTER_URL_PARAMS[key] ?? {};

		if (isPartUsed) {
			if (seoName !== undefined) {
				if (key === PARAM_KEYS.PRICE) {
					const { from, to, type } = params[key];
					switch (type) {
						case PRICE.FREE:
							path.push(PRICE.FREE);
							break;
						case PRICE.FROM:
							path.push(`cena-od-${from}-kc`);
							break;
						case PRICE.TO:
							path.push(`cena-do-${to}-kc`);
							break;
						case PRICE.BETWEEN:
							path.push(`cena-od-${from}-do-${to}-kc`);
							break;
						case PRICE.NO_LIMIT:
							path.push(PRICE.NO_LIMIT);
					}
				} else if (key === PARAM_KEYS.QUERY && !isEmpty(seoName)) {
					path.push(`${PARAM_KEYS.QUERY}/${encodeURIComponent(seoName)}`);
				} else {
					path.push(seoName);
				}
			}
		} else if (seoName !== undefined) {
			switch (key) {
				case PARAM_KEYS.WITH_PRICE_AGREEMENT:
					query['cena-dohodou'] = seoName;
					break;
				case PARAM_KEYS.ONLY_UNPACKED:
					query['rozbalene'] = seoName;
					break;
				case PARAM_KEYS.CATEGORY:
					if (params.category?.id !== 0 && params.category?.id !== undefined) {
						query.categoryId = params.category?.id.toString();
					}
					break;
				default:
					if (params[key]?.seoName !== defaultFilterParams[key]?.seoName) {
						query[key] = seoName;
					}
					break;
			}
		} else {
			switch (key) {
				case PARAM_KEYS.EXPIRING:
					if (params.expiring?.value === UPCOMING_EXPIRING_URL_VALUE) {
						query.expiring = String(UPCOMING_EXPIRING_URL_VALUE);
					}
					break;
				default:
					break;
			}
		}
	});

	const pathString = combinePath(path, isFullPath);
	// encode the rest of the params to query string
	const queryString = new URLSearchParams(query).toString();

	return `/${pathString}${queryString ? `?${queryString}` : ''}`.replace(/\/\//, '/');
}

export function getRedirectUrl(originalUrl: URL, validUrl: string): string | undefined {
	const [validUrlPathname, validUrlSearch] = validUrl.split('?');
	const validUrlSearchParams = new URLSearchParams(validUrlSearch);
	validUrlSearchParams.sort();
	originalUrl.searchParams.sort();

	const originalUrlSearchParamsWithoutUtm = filterSearchParams(
		originalUrl.searchParams,
		(key) => !key.startsWith('utm_'),
	);
	originalUrlSearchParamsWithoutUtm.sort();

	const hasUtmParams = hasUrlUtmParams(originalUrl);

	const shouldRedirect =
		originalUrl.pathname !== validUrlPathname ||
		(hasUtmParams
			? originalUrlSearchParamsWithoutUtm.toString() !== validUrlSearchParams.toString()
			: validUrlSearchParams.toString() !== originalUrl.searchParams.toString());

	if (!shouldRedirect) return;

	const utmSearchParams = filterSearchParams(originalUrl.searchParams, (key) => key.startsWith('utm_'));
	const concatChar = validUrl.includes('?') ? '&' : '?';
	return hasUtmParams ? validUrl + concatChar + utmSearchParams.toString() : validUrl;
}

function hasUrlUtmParams(url: URL) {
	return url.searchParams && [...url.searchParams.keys()].some((key) => key.startsWith('utm_'));
}

function filterSearchParams(params: URLSearchParams, condition: (key: string) => boolean) {
	const newParams = new URLSearchParams();

	for (const [key, value] of params) {
		if (condition(key)) {
			newParams.set(key, value);
		}
	}

	return newParams;
}

export function isRefererOnSameHost() {
	const referrerUrl = globalThis.window.document.referrer && new URL(globalThis.window.document.referrer);
	const currentUrl = new URL(globalThis.window.location.href);
	return !referrerUrl || referrerUrl.hostname === currentUrl.hostname;
}
